import { some } from '@utils/some';
import {
  DynamicPackagesFormStepNames,
  ExperienceType,
  FormStep,
  SelectedExperienceType,
  PackageType,
} from '../types';
import {
  getRepeatableExperienceOccurrenceDays,
  groupRepeatableExperiencesByDay,
} from './repeatableExperiencesUtils';

type Experience = {
  dayNumber: number;
  dayNumberRange: number[];
  delayAfter?: number;
  numberOfSelectionsRequired?: number;
  id: string;
  selectedIds?: string[];
  options?: {
    itemId: string;
    itemType: ExperienceType;
    name: string;
    meta: {
      entityId: string | number;
      entityType: string;
    };
  }[];
  meta?: {
    entityId: string | number;
    entityType: string;
  };
};

export function getExperienceSteps(
  experiences: Experience[],
  stayDuration: number,
  packageType: PackageType
): {
  steps: FormStep[];
  includedExperiences: SelectedExperienceType[];
} {
  const selectExperienceSteps = getSelectExperienceSteps(experiences, stayDuration, packageType);
  const includedExperiences = getIncludedExperiences(experiences, stayDuration, packageType);

  if (!some(selectExperienceSteps) && !some(includedExperiences)) {
    return {
      steps: [],
      includedExperiences: [],
    };
  }

  const totalTimeSlotsRequiredPerGuest = selectExperienceSteps.reduce((total, step) => {
    const dayTotal =
      step.selectExperienceOptionsByDayNumber?.reduce((daySum, dayGroup) => {
        const optionsTotal =
          dayGroup.options?.reduce((optSum, opt) => {
            if (opt.selectionsRequired === 0) {
              return optSum + (opt.options?.length || 0);
            } else {
              return optSum + (opt.selectionsRequired || 0);
            }
          }, 0) || 0;
        return daySum + optionsTotal;
      }, 0) || 0;
    return total + dayTotal;
  }, 0);

  return {
    steps: [
      ...selectExperienceSteps,
      {
        step: DynamicPackagesFormStepNames.EXPERIENCE,
        type: 'spa',
        totalTimeSlotsRequiredPerGuest,
      },
    ],
    includedExperiences,
  };
}

function getSelectExperienceSteps(
  experiences: Experience[],
  stayDuration: number,
  packageType: PackageType
): FormStep[] {
  const selectExperienceSteps = experiences.reduce((acc, experience) => {
    const options = experience?.options?.filter((o) => o.itemType !== 'spa-extras') || [];
    if (!some(options)) return acc;
    acc.push({
      options: (options || [])?.map((option) => ({
        id: Number(option.itemId),
        optionId: Number(option.meta.entityId),
        name: option.name,
        experienceType: option.itemType,
      })),
      selectionsRequired: experience.numberOfSelectionsRequired,
      optionGroupId: experience.meta?.entityId,
      delayAfter: experience.delayAfter,
      dayNumber: experience.dayNumber,
      dayNumberRange: experience.dayNumberRange,
    });
    return acc;
  }, [] as Omit<FormStep, 'step' | 'type'>[]);
  if (!selectExperienceSteps.length) return [];
  let selectExperienceOptionsByDayNumber: {
    dayNumber: number;
    options: FormStep['selectExperienceOptions'];
  }[] = [];

  if (packageType === PackageType.REPEATING) {
    selectExperienceOptionsByDayNumber = distributeRepeatingPackageExperiences(
      selectExperienceSteps,
      stayDuration
    );
  } else {
    selectExperienceSteps.forEach((experience) => {
      const existingDayNumberOptions = selectExperienceOptionsByDayNumber.find(
        (e) => e.dayNumber === experience.dayNumber
      );
      if (existingDayNumberOptions) {
        existingDayNumberOptions.options?.push(experience);
      } else {
        selectExperienceOptionsByDayNumber.push({
          dayNumber: experience.dayNumber || 0,
          options: [experience],
        });
      }
    });
  }
  return [
    {
      selectExperienceOptions: selectExperienceSteps,
      selectExperienceOptionsByDayNumber,
      step: DynamicPackagesFormStepNames.SELECT_SPA_EXPERIENCE,
      type: 'spa',
    },
  ];
}

function getIncludedExperiences(
  experiences: Experience[],
  stayDuration: number,
  packageType: PackageType
): SelectedExperienceType[] {
  const includedExperiences: SelectedExperienceType[] = [];
  experiences.forEach((experience) => {
    if (
      (!experience.numberOfSelectionsRequired || packageType === PackageType.REPEATING) &&
      !experience.options?.every((op) => op.itemType === 'spa-extras')
    ) {
      (experience?.options || []).forEach((option) => {
        if (option.itemType === 'spa-extras') return;

        const baseExperience = {
          id: Number(option.itemId),
          optionId: Number(option.meta.entityId),
          type: option.itemType,
          delayAfter: experience.delayAfter,
          optionGroupId: experience.meta?.entityId,
          count: 1,
          isIncluded: true,
          numberOfGroupSelectionsRequired: experience.numberOfSelectionsRequired || 1,
        };

        if (packageType === PackageType.REPEATING) {
          // For repeatable packages, distribute included experiences across multiple days
          const dayNumbers = getRepeatableExperienceOccurrenceDays({
            experienceDayNumber: experience.dayNumberRange?.[0] ?? experience.dayNumber ?? 0,
            stayDuration,
          });

          dayNumbers.forEach((dayNumber) => {
            includedExperiences.push({
              ...baseExperience,
              dayNumber,
              dayNumberRange: (experience.dayNumberRange || [experience.dayNumber || 0]).map(
                (rangeDay) => rangeDay + dayNumber
              ),
            });
          });
        } else {
          // For non-repeatable packages, use the original day number
          includedExperiences.push({
            ...baseExperience,
            dayNumber: experience.dayNumber,
            dayNumberRange: experience.dayNumberRange || [experience.dayNumber || 0],
          });
        }
      });
    }
  });
  return includedExperiences;
}

function distributeRepeatingPackageExperiences(
  selectExperienceSteps: Array<Omit<FormStep, 'type' | 'step'>>,
  stayDuration: number
): Array<{
  dayNumber: number;
  options: typeof selectExperienceSteps;
}> {
  const { day0, day1 } = groupRepeatableExperiencesByDay(selectExperienceSteps);

  const selectExperienceOptionsByDayNumber: Array<{
    dayNumber: number;
    options: typeof selectExperienceSteps;
  }> = [];

  for (let dayIndex = 0; dayIndex <= stayDuration; dayIndex++) {
    const optionsForThisDay: typeof selectExperienceSteps = [];

    // Day 0 experiences: Available on check-in through night before checkout
    const day0Days = getRepeatableExperienceOccurrenceDays({
      experienceDayNumber: 0,
      stayDuration,
    });
    if (day0Days.includes(dayIndex) && day0.length > 0) {
      optionsForThisDay.push(...day0.map((exp) => ({ ...exp, dayNumber: dayIndex })));
    }

    // Day 1 experiences: Available day after check-in through checkout
    const day1Days = getRepeatableExperienceOccurrenceDays({
      experienceDayNumber: 1,
      stayDuration,
    });
    if (day1Days.includes(dayIndex) && day1.length > 0) {
      optionsForThisDay.push(...day1.map((exp) => ({ ...exp, dayNumber: dayIndex })));
    }

    // Only add days that have at least one experience available
    if (optionsForThisDay.length > 0) {
      selectExperienceOptionsByDayNumber.push({
        dayNumber: dayIndex,
        options: optionsForThisDay,
      });
    }
  }

  return selectExperienceOptionsByDayNumber;
}
